import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { supabase } from '../lib/supabase';
import StarRating from '../components/StarRating';
import LoadingSpinner from '../components/LoadingSpinner';
import FollowButton from '../components/FollowButton';
import {
  ArrowLeft,
  User,
  Star,
  MapPin,
  Calendar,
  UserPlus,
  UserMinus,
  Clock,
  Lock,
  TrendingUp,
  Award,
  BarChart2,
  List,
  ChevronRight,
  Globe,
  Users,
  Eye,
  EyeOff,
  ShieldOff,
  ShieldAlert
} from 'lucide-react';
import { BadgeDisplay } from '../components/BadgeDisplay';
import { trackEvent } from '../lib/posthog';

function FriendProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    users,
    friends,
    following,
    followers,
    ratings,
    bars,
    getFriendStatus,
    getFollowStatus,
    sendFriendRequest,
    removeFriend,
    unfollowUser,
    acceptFriendRequest,
    acceptFollowRequest,
    denyFollowRequest,
    friendRequests,
    followRequests,
    loading,
    getUserVisibleLists,
    canViewUserContent,
    blockUser,
    unblockUser,
    isUserBlocked
  } = useApp();

  const [actionLoading, setActionLoading] = useState(false);
  const [showUnfollowModal, setShowUnfollowModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [userLists, setUserLists] = useState([]);
  const [listsLoading, setListsLoading] = useState(true);
  const [profilePrivacy, setProfilePrivacy] = useState(null); // null = loading
  const [canViewContent, setCanViewContent] = useState(false); // Default to hidden
  const [profileFollowerCount, setProfileFollowerCount] = useState(0);
  const [profileFollowingCount, setProfileFollowingCount] = useState(0);

  // Fetch user's profile data (privacy, follower/following counts)
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!userId) return;
      trackEvent('profile_viewed', { profile_user_id: userId });
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('profile_privacy, follower_count, following_count')
          .eq('id', userId)
          .single();

        if (!error && data) {
          setProfilePrivacy(data.profile_privacy || 'public');
          setProfileFollowerCount(data.follower_count || 0);
          setProfileFollowingCount(data.following_count || 0);
        }
      } catch (err) {
        console.error('Error fetching profile data:', err);
      }
    };
    fetchProfileData();
  }, [userId]);

  // Check if current user can view this profile's content
  useEffect(() => {
    const checkViewPermission = async () => {
      if (!userId) return;
      
      // Self can always view
      if (user?.id === userId) {
        setCanViewContent(true);
        return;
      }
      
      // Wait until profile privacy is loaded
      if (profilePrivacy === null) {
        setCanViewContent(false);
        return;
      }
      
      // Public profiles are always viewable
      if (profilePrivacy === 'public') {
        setCanViewContent(true);
        return;
      }
      
      // For private profiles, check if we have an ACTIVE follow (in the following array)
      // The following array should ONLY contain active follows, not pending ones
      const isActivelyFollowing = following?.includes(userId);
      
      // Double-check: if we have a pending outgoing request, we're NOT actively following
      const hasPendingRequest = followRequests?.outgoing?.some(r => r.following_id === userId);
      
      if (isActivelyFollowing && !hasPendingRequest) {
        setCanViewContent(true);
      } else {
        // Private profile and not actively following (pending or not following at all)
        setCanViewContent(false);
      }
    };
    checkViewPermission();
  }, [userId, user, following, profilePrivacy, followRequests]);

  // Fetch user's visible lists
  useEffect(() => {
    const fetchUserLists = async () => {
      if (!userId) return;
      setListsLoading(true);
      try {
        const lists = await getUserVisibleLists(userId);
        setUserLists(lists);
      } catch (error) {
        console.error('Error fetching user lists:', error);
      } finally {
        setListsLoading(false);
      }
    };
    fetchUserLists();
  }, [userId, getUserVisibleLists]);

  const getPrivacyIcon = (privacy) => {
    switch (privacy) {
      case 'public': return <Globe className="w-3 h-3 text-green-400" />;
      case 'friends': return <Users className="w-3 h-3 text-blue-400" />;
      default: return <Lock className="w-3 h-3 text-gray-400" />;
    }
  };

  const getListBarCount = (list) => {
    return list.list_bars?.length || 0;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Find the profile user
  const profileUser = users.find(u => u.id === userId);
  
  if (!profileUser) {
    return (
      <div className="min-h-screen bg-dive-black py-6 px-4">
        <div className="max-w-2xl mx-auto text-center py-12">
          <User className="w-16 h-16 text-dive-gray mx-auto mb-4" />
          <h2 className="text-white text-xl mb-2">User Not Found</h2>
          <p className="text-gray-400 text-sm mb-6">
            This user doesn't exist or their profile is private
          </p>
          <button onClick={() => navigate(-1)} className="btn-secondary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const isSelf = user?.id === userId;
  const friendStatus = getFriendStatus(userId);
  const followStatus = getFollowStatus ? getFollowStatus(userId) : 'not_following';
  const isFriend = friendStatus === 'friends';
  const isFollowing = followStatus === 'following' || following?.includes(userId);
  
  // Check if this user has sent YOU a follow request (incoming request)
  const hasIncomingRequest = followRequests?.incoming?.some(r => r.follower_id === userId);
  
  // Determine if we can see content (either following, public profile, or self)
  const canSeeContent = isSelf || canViewContent;
  
  // Get user's ratings (only if can view content)
  const userRatings = canSeeContent
    ? ratings.filter(r => r.userId === userId)
    : [];
  
  // Recent ratings (last 5)
  const recentRatings = [...userRatings]
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, 5);
  
  // Top rated bars (highest ratings)
  const topRatedBars = [...userRatings]
    .sort((a, b) => b.overall - a.overall)
    .slice(0, 5);
  
  // Calculate stats
  const totalRatings = userRatings.length;
  const avgRating = totalRatings > 0
    ? (userRatings.reduce((sum, r) => sum + r.overall, 0) / totalRatings).toFixed(1)
    : 0;
  
  // Most rated neighborhood
  const neighborhoodCounts = {};
  userRatings.forEach(r => {
    const bar = bars.find(b => b.id === r.barId);
    if (bar?.neighborhood) {
      neighborhoodCounts[bar.neighborhood] = (neighborhoodCounts[bar.neighborhood] || 0) + 1;
    }
  });
  const favoriteNeighborhood = Object.entries(neighborhoodCounts)
    .sort((a, b) => b[1] - a[1])[0]?.[0] || null;
  
  // Dive bar percentage
  const diveBarVotes = userRatings.filter(r => r.isDiveBar).length;
  const diveBarPercentage = totalRatings > 0
    ? Math.round((diveBarVotes / totalRatings) * 100)
    : 0;

  // Handle accepting incoming follow request (for private accounts)
  const handleAcceptFollowRequest = async () => {
    setActionLoading(true);
    try {
      // Check if there's an incoming follow request from this user
      const incomingFollowRequest = followRequests?.incoming?.find(r => r.follower_id === userId);
      if (incomingFollowRequest) {
        await acceptFollowRequest(userId);
      }
      // Legacy: check friend requests too
      const incomingFriendRequest = friendRequests?.incoming?.find(r => r.from_user_id === userId);
      if (incomingFriendRequest) {
        await acceptFriendRequest(incomingFriendRequest.id);
      }
    } catch (error) {
      console.error('Error:', error);
      alert(error.message || 'Failed to accept request');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dive-black py-6 pb-24">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        {/* Profile Card */}
        <div className="bg-dive-dark border border-dive-gray rounded-xl overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-dive-neon/30 to-dive-blue/20 p-6">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-dive-darker flex items-center justify-center border-2 border-dive-neon overflow-hidden">
                {profileUser.avatarUrl ? (
                  <img
                    src={profileUser.avatarUrl}
                    alt={profileUser.displayName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-3xl font-display text-dive-neon">
                    {profileUser.displayName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-display text-white tracking-wide">
                  {profileUser.displayName}
                </h1>
                <p className="text-gray-400">@{profileUser.username}</p>
                <div className="flex items-center gap-1 text-gray-500 text-sm mt-1">
                  <Calendar className="w-3 h-3" />
                  <span>Joined {new Date(profileUser.joinedAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Follow Action Button */}
          {!isSelf && user && (
            <div className="p-4 border-t border-dive-gray flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                {profilePrivacy === 'private' ? (
                  <>
                    <EyeOff className="w-4 h-4" />
                    <span>Private Account</span>
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4" />
                    <span>Public Account</span>
                  </>
                )}
              </div>
              
              {/* Show Accept/Deny if they've requested to follow YOU */}
              {hasIncomingRequest ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      try {
                        await denyFollowRequest(userId);
                      } catch (err) {
                        console.error('Error denying request:', err);
                      }
                    }}
                    className="px-3 py-1.5 text-sm bg-dive-gray text-gray-400 rounded-lg hover:bg-red-500/20 hover:text-red-400 transition-colors"
                  >
                    Deny
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await acceptFollowRequest(userId);
                      } catch (err) {
                        console.error('Error accepting request:', err);
                      }
                    }}
                    className="px-3 py-1.5 text-sm bg-dive-neon text-white rounded-lg hover:bg-dive-neon/80 transition-colors"
                  >
                    Accept
                  </button>
                </div>
              ) : (
                <FollowButton 
                  userId={userId} 
                  isPrivate={profilePrivacy === 'private'}
                  size="md"
                />
              )}

              {/* Block/Unblock button */}
              {user && userId !== user.id && (
                isUserBlocked(userId) ? (
                  <button
                    onClick={() => unblockUser(userId)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-600 text-gray-400 hover:text-white hover:border-white transition-colors"
                  >
                    <ShieldOff className="w-3.5 h-3.5" />
                    Unblock
                  </button>
                ) : (
                  <button
                    onClick={() => setShowBlockModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-700 text-gray-500 hover:text-red-400 hover:border-red-400 transition-colors"
                  >
                    <ShieldAlert className="w-3.5 h-3.5" />
                    Block
                  </button>
                )
              )}
            </div>
          )}

          {/* Stats - Always show follower/following counts */}
          <div className="grid grid-cols-4 border-t border-dive-gray">
            <div className="py-4 px-1 text-center border-r border-dive-gray">
              <div className="text-xl md:text-2xl font-bold text-dive-neon">{canSeeContent ? totalRatings : '—'}</div>
              <div className="text-gray-500 text-xs">Ratings</div>
            </div>
            <div className="py-4 px-1 text-center border-r border-dive-gray">
              <div className="text-xl md:text-2xl font-bold text-dive-blue">{profileFollowingCount}</div>
              <div className="text-gray-500 text-xs">Following</div>
            </div>
            <div className="py-4 px-1 text-center border-r border-dive-gray">
              <div className="text-xl md:text-2xl font-bold text-purple-400">{profileFollowerCount}</div>
              <div className="text-gray-500 text-xs">Followers</div>
            </div>
            <div className="py-4 px-1 text-center">
              <div className="text-xl md:text-2xl font-bold text-dive-green">{canSeeContent ? `${diveBarPercentage}%` : '—'}</div>
              <div className="text-gray-500 text-xs">Dive Bar</div>
            </div>
          </div>
        </div>

        {/* Content - Only shown if can view content */}
        {canSeeContent ? (
          <>
            {/* Tilted Top Three */}
            {userRatings.length > 0 && (
              <div className="mb-6">
                <h2 className="text-white font-display text-xl tracking-wide mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-dive-amber" />
                  TILTED TOP THREE
                </h2>
                <div className="space-y-2">
                  {[...userRatings]
                    .sort((a, b) => b.overall - a.overall)
                    .slice(0, 3)
                    .map(rating => {
                      const bar = bars.find(b => b.id === rating.barId);
                      if (!bar) return null;
                      return (
                        <Link
                          key={rating.id}
                          to={`/bar/${bar.id}`}
                          className="flex items-center justify-between p-3 bg-dive-dark border border-dive-gray rounded-lg hover:border-dive-neon/50 transition-colors"
                        >
                          <div className="flex-1">
                            <div className="text-white font-medium">{bar.name}</div>
                            <div className="text-gray-500 text-sm">{bar.neighborhood}</div>
                          </div>
                          <div className="flex items-center gap-1 text-dive-amber">
                            <Star className="w-4 h-4 fill-dive-amber" />
                            <span className="font-bold">{rating.overall.toFixed(1)}</span>
                          </div>
                        </Link>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Stats Card */}
            {favoriteNeighborhood && (
              <div className="bg-dive-dark border border-dive-gray rounded-xl p-4 mb-6">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-dive-blue" />
                  Stats
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-dive-darker rounded-lg p-3">
                    <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                      <MapPin className="w-4 h-4" />
                      Favorite Neighborhood
                    </div>
                    <p className="text-white font-medium">{favoriteNeighborhood}</p>
                  </div>
                  <div className="bg-dive-darker rounded-lg p-3">
                    <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                      <img src="/logo-icon.png" alt="" className="w-4 h-4 object-contain" />
                      Bars Visited
                    </div>
                    <p className="text-white font-medium">{totalRatings}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Badges Section */}
            <div className="mb-6">
              <BadgeDisplay userId={userId} />
            </div>

            {/* Recent Ratings */}
            {recentRatings.length > 0 && (
              <div className="mb-6">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-dive-amber" />
                  Recent Activity
                </h3>
                <div className="space-y-2">
                  {recentRatings.map(rating => {
                    const bar = bars.find(b => b.id === rating.barId);
                    return bar ? (
                      <Link
                        key={rating.id}
                        to={`/bar/${bar.id}`}
                        className="block bg-dive-dark border border-dive-gray rounded-lg p-3 hover:border-dive-neon/50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-white font-medium">{bar.name}</h4>
                            <p className="text-gray-500 text-sm">{bar.neighborhood}</p>
                          </div>
                          <div className="text-right">
                            <StarRating rating={rating.overall} size="sm" />
                            <p className="text-gray-500 text-xs mt-1">
                              {new Date(rating.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Top Rated Bars */}
            {topRatedBars.length > 0 && (
              <div className="mb-6">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Award className="w-5 h-5 text-dive-green" />
                  Top Rated Bars
                </h3>
                <div className="space-y-2">
                  {topRatedBars.map(rating => {
                    const bar = bars.find(b => b.id === rating.barId);
                    return bar ? (
                      <Link
                        key={rating.id}
                        to={`/bar/${bar.id}`}
                        className="block bg-dive-dark border border-dive-gray rounded-lg p-3 hover:border-dive-neon/50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-white font-medium">{bar.name}</h4>
                            <p className="text-gray-500 text-sm">{bar.neighborhood}</p>
                          </div>
                          <StarRating rating={rating.overall} size="sm" />
                        </div>
                      </Link>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* User's Lists */}
            {!listsLoading && userLists.length > 0 && (
              <div className="mb-6">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <List className="w-5 h-5 text-blue-400" />
                  {isSelf ? 'My Lists' : `${profileUser.displayName}'s Lists`}
                </h3>
                <div className="space-y-2">
                  {userLists.map(list => (
                    <Link
                      key={list.id}
                      to={`/lists/${list.id}`}
                      className="block bg-dive-dark border border-dive-gray rounded-lg p-3 hover:border-dive-neon/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-white font-medium">{list.title}</h4>
                            {getPrivacyIcon(list.privacy)}
                          </div>
                          {list.description && (
                            <p className="text-gray-500 text-sm mt-1 line-clamp-1">{list.description}</p>
                          )}
                          <p className="text-gray-600 text-xs mt-1">
                            {getListBarCount(list)} {getListBarCount(list) === 1 ? 'bar' : 'bars'}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {totalRatings === 0 && (
              <div className="text-center py-8 bg-dive-dark border border-dive-gray rounded-xl">
                <img src="/logo-icon.png" alt="" className="w-12 h-12 object-contain mx-auto mb-3 opacity-50" />
                <h3 className="text-white text-lg mb-2">No ratings yet</h3>
                <p className="text-gray-500 text-sm">
                  {isSelf ? "You haven't rated any bars yet!" : "This user hasn't rated any bars yet."}
                </p>
              </div>
            )}
          </>
        ) : (
          /* Private Profile - Not Following */
          <div className="flex flex-col items-center justify-center py-16 px-6 bg-dive-dark border border-dive-gray rounded-xl">
            <Lock className="w-16 h-16 text-dive-gray mb-4" />
            <h3 className="text-white text-xl font-medium mb-2">Private Account</h3>
            <p className="text-gray-400 text-center mb-6 max-w-xs">
              Follow {profileUser.displayName} to see their ratings and activity
            </p>
            {user && followStatus === 'not_following' && (
              <FollowButton 
                userId={userId} 
                isPrivate={true}
                size="lg"
              />
            )}
            {followStatus === 'pending' && (
              <div className="flex items-center gap-2 px-4 py-2 bg-dive-gray/50 rounded-lg text-gray-300">
                <Clock className="w-5 h-5" />
                <span>Follow request pending</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Block User Modal */}
      {showBlockModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-dive-dark border border-dive-gray rounded-xl max-w-sm w-full p-6">
            <h3 className="text-white text-lg font-medium mb-2">
              Block {profileUser?.displayName}?
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              They won't be able to see your content or interact with you. Their content will be hidden from your feed. You will also be unfollowed from each other.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowBlockModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setActionLoading(true);
                  try {
                    await blockUser(userId);
                    setShowBlockModal(false);
                  } catch (err) {
                    console.error('Error blocking user:', err);
                  } finally {
                    setActionLoading(false);
                  }
                }}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                {actionLoading ? 'Blocking...' : 'Block User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unfollow Modal */}
      {showUnfollowModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-dive-dark border border-dive-gray rounded-xl max-w-sm w-full p-6">
            <h3 className="text-white text-lg font-medium mb-2">
              Unfollow {profileUser.displayName}?
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              You will no longer see their activity in your feed.
              {profilePrivacy === 'private' && " If you want to follow them again, you'll need to send a new request."}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowUnfollowModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setActionLoading(true);
                  try {
                    await unfollowUser(userId);
                    setShowUnfollowModal(false);
                  } catch (err) {
                    console.error('Error unfollowing:', err);
                  } finally {
                    setActionLoading(false);
                  }
                }}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                {actionLoading ? 'Unfollowing...' : 'Unfollow'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FriendProfile;

